import { ViewChild, ChangeDetectorRef, Component, OnInit, ElementRef } from '@angular/core';
import { User } from "@app/models/user";
import { UserService } from "@app/services/user.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { MustMatch } from "@app/helpers/must-match.validator";
import { NotificationService } from "@app/services/notification.service";
import { AuthenticationService } from "@app/services/authentication.service";
import { AuthorizationService } from "@app/services/authorization.service";
import { MethodsHelper } from "@app/helpers/methods.helper";
import { Location } from '@angular/common';
import Cropper from 'cropperjs';
import { TranslateService } from '@ngx-translate/core';
import { BranchService } from '@app/services/branch.service';
import { Constants } from '@app/constants/constants';
import { Branch } from '@app/models/branch';

@Component({
    selector: 'app-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.css'],
    standalone: false
})
export class UserComponent implements OnInit {

    @ViewChild('fileInput') fileInput: ElementRef;
    currentUser: User;
    id: string;
    role: string;
    fullName: string;
    email: string;
    branchName: string;
    errorMessage: string | boolean;
    infoMessage: string;
    permissionsGroups: string [];
    roles: string[];
    branches: Array<Branch>;

    updatePasswordForm: FormGroup;
    submitted = false;
    oldPasswordEyeOpened = false;
    passwordEyeOpened = false;
    confirmPasswordEyeOpened = false;
    defaultAvatar: string = '../../../assets/img/avatar.png';
    imgSrc: string | ArrayBuffer;
    processingImgSrc: string | ArrayBuffer;
    hasDefaultAvatar: boolean = true;
    MAX_UPLOADED_FILE_SIZE = 1000000;
    selectedFile: File = null;
    IMAGE_TYPE_ERROR_MESSAGE = 'Please select an image file (PNG or JPEG)';
    IMAGE_UPLOAD_ERROR_MESSAGE = 'Image was not uploaded, please try again.';
    titleDialog: any;
    displayAlertDialog: any;
    displayAvatarDialog = false;

    croppable = false;
    cropper: any;

    constructor(private formBuilder: FormBuilder,
                private userService: UserService,
                private branchService: BranchService,
                private activatedRoute: ActivatedRoute,
                private notificationService: NotificationService,
                private router: Router,
                private authenticationService: AuthenticationService,
                private authorizationService: AuthorizationService,
                private cdRef: ChangeDetectorRef,
                private translate: TranslateService,
                private location: Location) {
        this.id = this.activatedRoute.snapshot.params.id;
        this.hasDefaultAvatar = (this.imgSrc == this.defaultAvatar);
    }

    ngOnInit() {
        const currentPath = this.location.path()?.toLocaleLowerCase();
        if (this.authorizationService.isAdmin()) {
            if (currentPath?.indexOf("/user") == 0 && !currentPath?.includes("/user/settings/")) {
              this.router.navigate(["/admin"]);
            }
        } else {
            if (currentPath?.indexOf("/admin") == 0 || currentPath === "" || currentPath === "/") {
              this.router.navigate([this.authorizationService.getFirstAccessibleUserRoute()]);
            }
        }
        this.getBranches();
        this.getCurrentUserInfo();
        if(this.id){
            this.downloadFile(this.id);
            this.getCurrentUser();
        }

        this.updatePasswordForm = this.formBuilder.group({

            oldPassword: ['', [Validators.required]],
            password: ['', Validators.compose([
                Validators.required,
                Validators.pattern('^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[^a-zA-Z\\d]).{9,}$')
            ])],
            confirmPassword: ['', Validators.required]
        }, {
            validator: MustMatch('password', 'confirmPassword')
        });
    }

    public getCurrentUser(): any {
        this.userService.getById(this.id).subscribe({
            next: res => {
                this.currentUser = res;
                this.email = this.currentUser.email;
                this.fullName = this.currentUser.firstName + " " + this.currentUser.lastName;
                this.updateBranchName();
            },
            error: (err) => this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(err)}`)
        });
    }

    getBranches(): void {
        const pageOption = {
            size: Constants.PAGE_UNLIMITED_ITEM_COUNT,
            page: 0
        }    
        const pageParams = { ...pageOption };
        this.branchService.getList(pageParams).subscribe({
            next: (data) => {
                this.branches = data.content;
                this.updateBranchName();
            },
            error: (err) => this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(err)}`)
        });
    }

    private updateBranchName(): void {
        this.branchName = this.branches?.find(branch => branch.id === this.currentUser?.branchId)?.name;
    }

    get f() {
        return this.updatePasswordForm.controls;
    }

    onSubmit() {
        this.submitted = true;

        if (this.updatePasswordForm.invalid) {
            return;
        } else {
            if (this.id) {
                let userDetails = {
                    "oldPassword": this.updatePasswordForm.value.oldPassword,
                    "password": this.updatePasswordForm.value.password,
                };

                this.userService.updatePassword(this.id, userDetails).subscribe({
                    next: () => {
                        this.notificationService.showSuccessMessage(this.translate.instant('message.password_change_success'), '');

                        this.updatePasswordForm.reset();
                        this.authenticationService.logout();
                        this.router.navigate(['/login']);
                    },
                    error: (err) => this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(err)}`)
                });
            }
        }
    }

    public getCurrentUserInfo(): any {
        const userInfo = this.authenticationService.getCurrentUserInfo();
        if (userInfo) {
            this.id = userInfo.jti;
            const userRoles = this.authenticationService.getUserRoles();
            this.permissionsGroups = userRoles ? JSON.parse(userRoles) : [];
            this.roles = userInfo.roles;
            return userInfo;
        }
    }

    returnToMainPage() {
        const url = this.authorizationService.isAdmin() ? '/admin' : this.authorizationService.getFirstAccessibleUserRoute();
        this.router.navigateByUrl(url);
    }

    toggleOldPasswordEyeOpened(event, opened) {
        event.preventDefault();
        this.oldPasswordEyeOpened = opened;
    }

    togglePasswordEyeOpened(event, opened) {
        event.preventDefault();
        this.passwordEyeOpened = opened;
    }

    toggleConfirmPasswordEyeOpened(event, opened) {
        event.preventDefault();
        this.confirmPasswordEyeOpened = opened;
    }

    updateCropImage() {
        setTimeout(() => {
            if (this.cropper) {
                this.cropper.setData({
                    x: 0,
                    y: 0,
                    width: null,
                    height: null
                  });
            } else {
                this.cropper = new Cropper(document.getElementById("imageProfile") as HTMLImageElement, {
                    background: false,
                    guides: false,
                    aspectRatio: 1,
                    ready: () => {
                        this.croppable = true;
                    },
                });
            }
          }, 500)
    }

    onFileSelected(event: any) {
        this.selectedFile = event.target.files[0] as File;
        if (!['image/png', 'image/jpeg'].includes(this.selectedFile.type)) {
            this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(this.IMAGE_TYPE_ERROR_MESSAGE)}`);
            return;
        }
        const reader = new FileReader();

        reader.onload = (e) => {
            this.processingImgSrc = e.target.result;
            if (this.cropper) {
                this.cropper.destroy();
            }
            this.cropper = null;
            this.updateCropImage();
            
        };
        reader.readAsDataURL(this.selectedFile);
    }

    onUpload() {
        if (!this.imgSrc) {
            console.log('No file selected.');
            return;
        }
            
            const formData = new FormData();
            formData.append('file', this.convertStringToBlob(this.imgSrc), this.selectedFile?this.selectedFile.name : "old_image.png");
            formData.append('userId', this.id);
            this.userService.upload(formData, this.id).subscribe({
                 next: () => {
                    this.notificationService.showSuccessMessage(this.translate.instant('message.image_upload_success'), '');
                    this.reloadPage();
                },
                error: () => this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(this.IMAGE_UPLOAD_ERROR_MESSAGE)}`)
            });
    }

    toggleUploadVisibility() {
        this.processingImgSrc = this.imgSrc;
        this.displayAvatarDialog = true;
        this.updateCropImage();
    }

    downloadFile(id) {
        this.userService.download(id).subscribe({
            next: (blob) => {
                if(!(blob instanceof Blob)){
                    this.imgSrc = this.defaultAvatar;
                    this.hasDefaultAvatar = true;
                }else {
                    let reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        let base64data = reader.result as string;
                        this.imgSrc = base64data.replace('data:application/octet-stream;', 'data:image/png;');
                        this.cdRef.detectChanges();
                    }
                }
            },
            error: (e) => {
                this.imgSrc = this.defaultAvatar;
                this.hasDefaultAvatar = true;
            }
        });
    }

    reloadPage() {
        window.location.reload();
    }

    removePortfolioImage() {
        if (this.id && !this.hasDefaultAvatar) {
            this.displayAlertDialog = true;
        }
    }

    confirmDelete() {
        this.userService.remove(this.id).subscribe({
            next: () => {
                this.hasDefaultAvatar = false;
                this.notificationService.showSuccessMessage(this.translate.instant('message.image_delete_success'), "");
                this.reloadPage();
            },
            error: (err) => this.notificationService.showErrorMessage(`${MethodsHelper.showErrorMessageStyling(err)}`)
        });
    }

    createRoundedAvatar() {
        if (!this.croppable) {
            return;
        }

        const croppedCanvas = this.cropper.getCroppedCanvas();
        const roundedCanvas = this.getRoundedCanvas(croppedCanvas);
        this.imgSrc = roundedCanvas.toDataURL();
        this.hideAvatarDialog();
        this.onUpload();
    }
    
    getRoundedCanvas(sourceCanvas: HTMLCanvasElement) {
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        var width = sourceCanvas.width;
        var height = sourceCanvas.height;

        canvas.width = width;
        canvas.height = height;
        context.imageSmoothingEnabled = true;
        context.drawImage(sourceCanvas, 0, 0, width, height);
        context.globalCompositeOperation = 'destination-in';
        context.beginPath();
        context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI, true);
        context.fill();
        return canvas;
    }

    hideAvatarDialog() {
        if (this.cropper) {
            this.cropper.destroy();
        }
        this.cropper = null;
        this.displayAvatarDialog = false;
    }

    openFileDialog() {
        this.fileInput.nativeElement.click();
    }

    convertStringToBlob(base64String) {

        // Split the base64 string to extract data and mimetype
        const parts = base64String.split(';base64,');
        const contentType = parts[0].split(':')[1];
        const base64Data = parts[1];

        // Convert base64 to Uint8Array
        const byteString = atob(base64Data);
        const byteNumbers = new Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            byteNumbers[i] = byteString.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        // Create a Blob object from the base64 data and mimetype
        const blob = new Blob([byteArray], { type: contentType });

        return blob;
    }
}
